// src/pages/AcceptTerms.js
//
// Two entry points share this same component:
//   1. Token mode (default): reached via /accept-terms?token=<hex-token>
//      in the ToS acceptance email, sent after a platform admin approves
//      an organizer request (or after organizer self-signup email
//      verification).
//   2. Session mode (sessionMode prop, from App.js): a post-login gate
//      for an already-authenticated organizer whose tos_status is
//      'outdated' or 'never_accepted' -- no token needed, since the
//      session itself identifies who's accepting. Exists so a buried or
//      spam-filtered acceptance email doesn't leave someone stuck with no
//      other way back into the app; the token-based email flow still
//      exists and still works, this is a second path to the same result.
//
// Unlike VerifyOrganizer, this does NOT auto-submit on mount -- accepting
// Terms is a deliberate action requiring the person to actually see what
// they're agreeing to, not just prove they own an email address or a
// session.

import React, { useState, useEffect, useRef } from 'react';
import { acceptTos, acceptOrganizerTosSession, getTermsOfService, getPrivacyPolicy, getLegalMeta } from '../services/api';
import SimpleMarkdown from '../components/SimpleMarkdown';

function AcceptTerms({ onBackToLogin, sessionMode = false, onAccept, onLogout }) {
    const token = new URLSearchParams(window.location.search).get('token');

    const [terms,   setTerms]   = useState('');
    const [privacy, setPrivacy] = useState('');
    const [meta,    setMeta]    = useState(null); // { terms: {version, published_at}, privacy: {...} } | null
    const [activeDoc, setActiveDoc] = useState('terms'); // 'terms' | 'privacy'
    const [docsLoading, setDocsLoading] = useState(true);
    const scrollRef = useRef(null);

    const [checked,   setChecked]   = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status,    setStatus]    = useState('reviewing'); // 'reviewing' | 'success' | 'error'
    const [error,     setError]     = useState('');

    useEffect(() => {
        Promise.all([getTermsOfService(), getPrivacyPolicy(), getLegalMeta()])
            .then(([t, p, m]) => {
                setTerms(t.data);
                setPrivacy(p.data);
                setMeta(m.data);
            })
            .catch(() => setError('Failed to load the Terms of Service / Privacy Policy. Please try refreshing.'))
            .finally(() => setDocsLoading(false));
    }, []);

    // Formats an ISO timestamp as e.g. "July 25, 2026" -- no need for a
    // date library for a single, simple display case.
    function formatDate(isoString) {
        if (!isoString) return null;
        return new Date(isoString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    const activeMeta = meta ? meta[activeDoc] : null;

    // Reset scroll to the top whenever switching between Terms and Privacy
    // -- without this, switching tabs after scrolling down in one kept the
    // same scroll offset in the other, landing the reader mid-document
    // instead of at the top.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [activeDoc]);

    const handleAccept = async () => {
        if (!sessionMode && !token) {
            setStatus('error');
            setError('This link is missing its token. Please use the link from your email.');
            return;
        }
        setSubmitting(true);
        try {
            if (sessionMode) {
                await acceptOrganizerTosSession();
            } else {
                await acceptTos(token);
            }
            setStatus('success');
        } catch (err) {
            setStatus('error');
            setError(err.response?.data?.error || 'Something went wrong. Please try again or contact support.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-2xl">
                <div className="text-center mb-6">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                        <div className="text-5xl font-black text-white tracking-tight">
                            Hebert<span className="text-orange-500">Hoops</span>
                        </div>
                    </div>
                    <p className="text-gray-400">
                        {sessionMode
                            ? 'Our Terms of Service and/or Privacy Policy have been updated'
                            : 'Accept Terms of Service & Privacy Policy'}
                    </p>
                </div>

                <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 border border-gray-800">
                    {status === 'success' ? (
                        <div className="space-y-4">
                            <div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg text-sm">
                                <p className="font-bold mb-1">Terms accepted!</p>
                                <p>
                                    {sessionMode
                                        ? 'Your organizer account is fully active again.'
                                        : 'Your organizer account is now fully active. You can log in and start creating tournaments and pools.'}
                                </p>
                            </div>
                            <button
                                onClick={sessionMode ? onAccept : onBackToLogin}
                                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                {sessionMode ? 'Continue' : 'Go to sign in'}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2 mb-4 border-b border-gray-800">
                                <button
                                    onClick={() => setActiveDoc('terms')}
                                    className={'px-4 py-2 text-sm font-bold border-b-2 transition-colors ' +
                                        (activeDoc === 'terms' ? 'border-orange-500 text-orange-400' : 'border-transparent text-gray-500 hover:text-gray-300')}>
                                    Terms of Service
                                </button>
                                <button
                                    onClick={() => setActiveDoc('privacy')}
                                    className={'px-4 py-2 text-sm font-bold border-b-2 transition-colors ' +
                                        (activeDoc === 'privacy' ? 'border-orange-500 text-orange-400' : 'border-transparent text-gray-500 hover:text-gray-300')}>
                                    Privacy Policy
                                </button>
                            </div>

                            <div ref={scrollRef} className="bg-gray-950 border border-gray-800 rounded-lg p-4 h-80 overflow-y-auto mb-5">
                                {docsLoading ? (
                                    <p className="text-gray-500 text-sm">Loading…</p>
                                ) : (
                                    <>
                                        {activeMeta && (
                                            <p className="text-gray-500 text-xs italic mb-3">
                                                Last updated: {formatDate(activeMeta.published_at)} — Version: {activeMeta.version}
                                            </p>
                                        )}
                                        <SimpleMarkdown content={activeDoc === 'terms' ? terms : privacy} />
                                    </>
                                )}
                            </div>

                            {status === 'error' && (
                                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm mb-4">
                                    {error}
                                </div>
                            )}

                            <label className="flex items-start gap-3 mb-5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={e => setChecked(e.target.checked)}
                                    className="mt-1 rounded"
                                />
                                <span className="text-gray-300 text-sm">
                                    I have read and agree to the Terms of Service and Privacy Policy above.
                                </span>
                            </label>

                            <button
                                onClick={handleAccept}
                                disabled={!checked || submitting}
                                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                {submitting ? 'Submitting…' : 'Accept and Continue'}
                            </button>

                            {sessionMode && onLogout && (
                                <button
                                    onClick={onLogout}
                                    className="w-full text-gray-500 hover:text-gray-300 text-sm mt-4 transition-colors"
                                >
                                    Sign out instead
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AcceptTerms;